import React, { useState, useMemo, useCallback } from 'react';
import { BorderedWrapper } from './style';
import { Grid, Typography } from '@mui/material';
import { useSelector, shallowEqual } from 'react-redux';
import { RootStateType } from '../../store';
import moment from 'moment';

import DownloadShipmentLabelAndInvoice from '@Components/DownloadShipmentLabelAndInvoice';
import ShipmentKycUpload from '@Components/ShipmentKycUpload';
import { CSP_TYPES, hideDownloadCarrierAWBAction } from '@Constants/Shipment';

export function ShipmentHistoryActions() {
  const [documentUploadModalOpen, setDocumentUploadModalOpen] = useState(false);

  // ✅ Single selector with shallowEqual (reduces re-renders)
  const { shipmentData, adminShipmentData } = useSelector(
    (state: RootStateType) => ({
      shipmentData: state.shipment.shipmentById,
      adminShipmentData: state.adminShipment.shipmentById,
    }),
    shallowEqual,
  );

  // ✅ Memoized values
  const shipmentNumber = shipmentData?.shipmentNumber || '';

  const formattedDate = useMemo(
    () => moment(shipmentData?.createdAt).format('DD MMMM YYYY'),
    [shipmentData?.createdAt],
  );

  const placedBy = shipmentData?._placedById?.name;

  const vendorShipmentModel = shipmentData?.vendorShipmentModel || adminShipmentData?.vendorShipmentModel;

  // ✅ Convert long OR chain into Set lookup (faster & cleaner)
  const allowedCarrierCodes = useMemo(
    () =>
      new Set([
        CSP_TYPES.fedex.value,
        CSP_TYPES.ithinklogistics.value,
        CSP_TYPES.ithink_logistics_domestic.value,
        CSP_TYPES.linex.value,
        CSP_TYPES.delhivery.value,
        CSP_TYPES.trackon.value,
        CSP_TYPES.ups.value,
        CSP_TYPES.emirates_skycargo.value,
        CSP_TYPES.dpduk.value,
        CSP_TYPES.shree_nandan_courier_limited.value,
        CSP_TYPES.dhlIndia.value,
        'cirro',
        CSP_TYPES.stage.value,
        CSP_TYPES.bluedart.value,
        CSP_TYPES.universal.value,
        CSP_TYPES.aramex.value,
        CSP_TYPES.dhl.value,
      ]),
    [],
  );

  const carrierAWBIcon = useMemo(() => {
    const code = shipmentData?.buyingPricing?.code;

    if (vendorShipmentModel && code && allowedCarrierCodes.has(code)) {
      return shipmentData?.buyingPricing?.serviceImage;
    }
    return '';
  }, [shipmentData, vendorShipmentModel, allowedCarrierCodes]);

  const hideCarrierAWB = useMemo(() => hideDownloadCarrierAWBAction(shipmentData), [shipmentData]);

  // ✅ Stable callbacks
  const handleOpen = useCallback(() => {
    setDocumentUploadModalOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setDocumentUploadModalOpen(false);
  }, []);

  return (
    <BorderedWrapper className="mb-2">
      <Grid container justifyContent="space-between">
        <Grid item>
          <Typography style={{ fontSize: '32px' }} className="font-semibold">
            {shipmentNumber}
          </Typography>

          <Typography className="md-text">{formattedDate}</Typography>

          <Typography className="md-text" textTransform="capitalize">
            Placed By: {placedBy}
          </Typography>
        </Grid>

        {documentUploadModalOpen && (
          <ShipmentKycUpload shipmentNumber={shipmentNumber} open={documentUploadModalOpen} handleClose={handleClose} />
        )}

        <DownloadShipmentLabelAndInvoice
          handleView={handleOpen}
          shipmentNumber={shipmentNumber}
          hideCarrierAWB={hideCarrierAWB}
          carrierAWBIcon={carrierAWBIcon}
        />
      </Grid>
    </BorderedWrapper>
  );
}

export default ShipmentHistoryActions;
